import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { DebugUiBridge } from '../DebugUiBridge';

interface CharacterInfo {
  id: string;
  hp: { current: number; max: number };
  position: { x: number; y: number; z: number };
  velocity: { x: number; y: number; z: number };
  isGrounded: boolean;
  playerT: number;
  facingDirection: number;
}

/**
 * Character Panel
 * 
 * Shows live stats for all characters in the party:
 * - Health (HP)
 * - Position
 * - Velocity
 * - Grounded state
 * - Spline position (T)
 * - Facing direction
 */
@customElement('character-panel')
export class CharacterPanel extends LitElement {
  static styles = css`
    :host {
      display: block;
      height: 100%;
      color: #eee;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 12px;
    }

    .panel-content {
      display: flex;
      flex-direction: column;
      height: 100%;
      padding: 12px;
      gap: 8px;
      overflow-y: auto;
    }

    .section-title {
      font-weight: 600;
      font-size: 11px;
      text-transform: uppercase;
      opacity: 0.75;
      letter-spacing: 0.5px;
      margin: 8px 0 4px 0;
    }

    .character-card {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 4px;
      padding: 10px;
      margin-bottom: 8px;
    }

    .character-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
      padding-bottom: 6px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    .character-name {
      font-weight: 600;
      font-size: 13px;
    }

    .character-id {
      font-size: 10px;
      opacity: 0.6;
    }

    .hp-bar-container {
      background: rgba(0, 0, 0, 0.3);
      border-radius: 3px;
      height: 20px;
      margin: 6px 0;
      overflow: hidden;
      position: relative;
    }

    .hp-bar {
      height: 100%;
      transition: width 0.3s ease;
      border-radius: 3px;
    }

    .hp-bar.healthy {
      background: linear-gradient(90deg, #4caf50, #66bb6a);
    }

    .hp-bar.damaged {
      background: linear-gradient(90deg, #ff9800, #ffa726);
    }

    .hp-bar.critical {
      background: linear-gradient(90deg, #f44336, #e57373);
    }

    .hp-text {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-weight: 600;
      font-size: 11px;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
      pointer-events: none;
    }

    .stat-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 6px;
      margin-top: 8px;
    }

    .stat-item {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .stat-label {
      font-size: 10px;
      opacity: 0.6;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }

    .stat-value {
      font-family: 'Courier New', monospace;
      font-size: 11px;
      color: #4fc3f7;
    }

    .state-badges {
      display: flex;
      gap: 4px;
      margin-top: 6px;
      flex-wrap: wrap;
    }

    .badge {
      padding: 2px 6px;
      border-radius: 3px;
      font-size: 10px;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }

    .badge.grounded {
      background: rgba(76, 175, 80, 0.3);
      color: #81c784;
    }

    .badge.airborne {
      background: rgba(33, 150, 243, 0.3);
      color: #64b5f6;
    }

    .badge.forward {
      background: rgba(156, 39, 176, 0.3);
      color: #ba68c8;
    }

    .badge.backward {
      background: rgba(255, 152, 0, 0.3);
      color: #ffb74d;
    }

    .no-data {
      opacity: 0.5;
      text-align: center;
      padding: 20px;
    }
  `;

  @state()
  private characters: CharacterInfo[] = [];

  private eventListener?: (e: Event) => void;
  private updateInterval: number | null = null;

  connectedCallback() {
    super.connectedCallback();
    this.startUpdates();
    
    // Subscribe to character list updates
    this.eventListener = (e: Event) => {
      const ce = e as CustomEvent;
      const event = ce.detail;
      
      if (event?.type === 'characters:list') {
        this.characters = event.characters || [];
      }
    };
    
    window.addEventListener(DebugUiBridge.uiEventName, this.eventListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.stopUpdates();
    if (this.eventListener) {
      window.removeEventListener(DebugUiBridge.uiEventName, this.eventListener);
    }
  }

  private startUpdates() {
    // Update character data every 100ms for smooth HP bars
    this.updateInterval = window.setInterval(() => {
      this.updateCharacterData();
    }, 100);
  }

  private stopUpdates() {
    if (this.updateInterval !== null) {
      window.clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
  }

  private updateCharacterData() {
    DebugUiBridge.dispatchCommand({ type: 'characters:get' });
  }

  private getHpPercentage(hp: { current: number; max: number }): number {
    if (hp.max === 0) return 0;
    return (hp.current / hp.max) * 100;
  }

  private getHpClass(percentage: number): string {
    if (percentage > 60) return 'healthy';
    if (percentage > 30) return 'damaged';
    return 'critical';
  }

  render() {
    return html`
      <div class="panel-content">
        <div class="section-title">Party Characters</div>

        ${this.characters.length === 0
          ? html`<div class="no-data">No characters loaded</div>`
          : this.characters.map(char => this.renderCharacter(char))
        }
      </div>
    `;
  }

  private renderCharacter(char: CharacterInfo) {
    const hpPercent = this.getHpPercentage(char.hp);
    const hpClass = this.getHpClass(hpPercent);

    return html`
      <div class="character-card">
        <div class="character-header">
          <div class="character-name">${char.id}</div>
        </div>

        <!-- HP Bar -->
        <div class="hp-bar-container">
          <div class="hp-bar ${hpClass}" style="width: ${hpPercent}%"></div>
          <div class="hp-text">
            ${char.hp.current} / ${char.hp.max} HP
          </div>
        </div>

        <!-- Stats Grid -->
        <div class="stat-grid">
          <div class="stat-item">
            <div class="stat-label">Position</div>
            <div class="stat-value">
              ${char.position.x.toFixed(1)}, ${char.position.y.toFixed(1)}, ${char.position.z.toFixed(1)}
            </div>
          </div>

          <div class="stat-item">
            <div class="stat-label">Velocity</div>
            <div class="stat-value">
              ${char.velocity.x.toFixed(1)}, ${char.velocity.y.toFixed(1)}, ${char.velocity.z.toFixed(1)}
            </div>
          </div>

          <div class="stat-item">
            <div class="stat-label">Spline T</div>
            <div class="stat-value">${char.playerT.toFixed(3)}</div>
          </div>

          <div class="stat-item">
            <div class="stat-label">Facing</div>
            <div class="stat-value">${char.facingDirection > 0 ? 'Forward (+T)' : 'Backward (-T)'}</div>
          </div>
        </div>

        <!-- State Badges -->
        <div class="state-badges">
          <div class="badge ${char.isGrounded ? 'grounded' : 'airborne'}">
            ${char.isGrounded ? 'Grounded' : 'Airborne'}
          </div>
          <div class="badge ${char.facingDirection > 0 ? 'forward' : 'backward'}">
            ${char.facingDirection > 0 ? '→ Forward' : '← Backward'}
          </div>
        </div>
      </div>
    `;
  }
}
